import type { HttpCore } from '../http';
import type { PushSubscriptionRequest, PushSubscriptionSummary, VapidPublicKey } from '../types';

/**
 * Web Push: the application server's public identity, and this browser's subscription.
 *
 * `vapidPublicKey` is the one call here that needs no token — a browser has to read the key before it has
 * anything to subscribe with, and it is a public key. It is served from the API rather than baked into the web
 * image so there is one source of truth per environment and no front-end rebuild to rotate it.
 */
export function createPushApi(http: HttpCore) {
  return {
    vapidPublicKey: () => http.get<VapidPublicKey>('/push/vapid-public-key'),

    /** Idempotent by endpoint: calling it again for the same browser updates one row. */
    subscribe: (body: PushSubscriptionRequest) =>
      http.post<PushSubscriptionSummary>('/me/push-subscriptions', body),

    /** Idempotent: a subscription the server has already pruned still resolves. */
    unsubscribe: (endpoint: string) =>
      http.del<void>(`/me/push-subscriptions?endpoint=${encodeURIComponent(endpoint)}`),
  };
}

export type PushApi = ReturnType<typeof createPushApi>;
